import {
  Component,
  HostListener,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AppConfig, appConfigService } from '@app/config';
import { TreeViewProviderDirective } from '@app/providers/tree-view-provider.service';
import { ListViewProviderDirective, UIComponents, ViewData } from '@ntap/core';
import { MessageService, TreeDragDropService, TreeNode } from 'primeng/api';
import { Tree } from 'primeng/tree';
import { map, Observable, Subscription, tap } from 'rxjs';
import { HierarhijaComponent } from '../hierarhija/hierarhija.component';
import { EarhivTreeNode } from '../hierarhija/types';
import { ParentListViewProvider } from '../pretraga-kataloga/providers/parent-list-view.provider';

export const DEFAULT_PAGE_LIMIT = 600;

export const INITAL_ROOT_VIEWSTATE = {
  $paging_offset: 0,
  $paging_limit: DEFAULT_PAGE_LIMIT
};

@Component({
  selector: 'app-hierarhija-klasifikacija',
  templateUrl: './hierarhija-klasifikacija.component.html',
  styleUrls: ['./hierarhija-klasifikacija.component.scss'],
  providers: [
    TreeDragDropService,
    MessageService,
    TreeViewProviderDirective,
    {
      provide: ListViewProviderDirective,
      useExisting: TreeViewProviderDirective
    },
    {
      provide: ParentListViewProvider,
      useExisting: TreeViewProviderDirective
    }
  ]
})
export class HierarhijaKlasifikacijaComponent implements OnInit, OnDestroy {
  @ViewChild(Tree)
  tree: Tree;
  showDeleteButton = false;
  loading = false;
  selectedNodesCount = 0;
  tableToolbarCompId = UIComponents.LISTTOOLBAR;
  tableToolbarCompId1 = UIComponents.LISTACTIONBAR;

  files1$: Observable<TreeNode[]>;
  _selectedFiles: TreeNode[];
  actions$: Observable<any[]>;
  loadingState$: Observable<boolean>;
  displayItems$: Observable<ViewData>;

  propagateSelectionUp = false;
  propagateSelectionDown: boolean;

  private route = inject(ActivatedRoute);
  private routeState: any = {};
  private navigationSubscription: Subscription;

  @HostListener('keydown.shift', ['$event'])
  onKeyDown() {
    this.propagateSelectionDown = true;
  }

  @HostListener('keyup.shift', ['$event'])
  onKeyUp() {
    this.propagateSelectionDown = false;
  }

  constructor(
    @Inject(appConfigService) appConfig: AppConfig,
    private msg: MessageService,
    private router: Router,
    private listViewProvider: TreeViewProviderDirective
  ) {
    console.log('appConfig.apiHost: ' + appConfig.apiHost);
    this.actions$ = this.listViewProvider.view$.pipe(
      map((response: any) => {
        return response.actions;
      })
    );
    this.loadingState$ = this.listViewProvider.loadingState$;
    this.files1$ = this.listViewProvider.treeViewData$;
    this.listViewProvider.viewName = null as any;
    this.listViewProvider.viewState = null as any;
    this.listViewProvider.mapRoots = this.mapRoots;
    this.listViewProvider.mapNodes = this.mapNodes;
  }

  ngOnInit() {
    this.initNavigationStateSubscription();
    this.listViewProvider.rootViewName =
      'pregled_strukture_glavna_klasifikacija';
    this.listViewProvider.rootViewState = this.getInitRootViewState();
    this.selectedFiles = [];
    this.listViewProvider.viewName = 'jedinica_gradiva';
    this.listViewProvider.viewState = {
      $paging_offset: 0,
      $paging_limit: 100
    };
  }

  ngOnDestroy(): void {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  public mapRoots = (rootsResponse: Observable<any[]>): Observable<any[]> => {
    return rootsResponse.pipe(
      map((roots) => {
        const rootNodes: EarhivTreeNode[] = roots
          .filter((element) => !element['sif~glavna_klasifikacija~parent_id'])
          .map((element: any) => {
            const node: EarhivTreeNode = {};
            node['data'] = element;
            node['label'] =
              element['sif~glavna_klasifikacija~sifra'] +
                ' ' +
                element['sif~glavna_klasifikacija~naziv'] || '';
            node['icon'] = 'pi pi-fw pi-book';
            node['children'] = [];
            node['leaf'] = false;
            node['selectable'] = false;
            node['offset'] = 0;
            return node;
          });

        const mapped: TreeNode[] = rootNodes.map((rootNode: any) => {
          return this.mapRootChildren(roots, rootNode);
        });

        return mapped;
      })
    );
  };

  public mapNodes = (response: any, parentNode?: EarhivTreeNode): any => {
    const loadedNodes: EarhivTreeNode[] = [];
    //brisanje loadMoreNode da se ne bi ponavljali u listi
    parentNode?.children?.pop();

    response.forEach((element: any) => {
      const node: any = {};
      node['data'] = element;
      node['label'] =
        (element['en~jedinica_gradiva~arhivska_oznaka'] || '') +
        ' ' +
        (element['en~jedinica_gradiva~glavni_naziv'] || '');
      node['icon'] = 'pi pi-fw pi-file';
      node['children'] = [];
      node['lockedIconClass'] = element['en~jedinica_gradiva~locked']
        ? 'lock-status p-treenode-icon pi pi-lock'
        : 'lock-status p-treenode-icon pi pi-lock-open';
      node['children'] = [];
      node['leaf'] = false;
      node['selectable'] = true;
      node['id'] = HierarhijaComponent.getElementId(element);
      node['offset'] = 0;

      // definiramo vezu prema parent node-u na podacima, ali preko defineProperty da izbjegnemo cirkularnu serijalizaciju u JSON
      Object.defineProperty(element, '$parentNode', {
        value: 'static',
        writable: true
      });

      element.$parentNode = parentNode;

      loadedNodes.push(node);
    });

    // dodajemo loadMoreNode ako imamo neki response
    if (
      loadedNodes.length > 0 &&
      loadedNodes[0].data.$itemsCount > loadedNodes.length
    ) {
      const loadMoreNode: any = {
        type: 'url',
        selectable: false,
        draggable: false,
        droppable: false,
        label: 'Učitaj više.',
        // icon: "pi pi-arrow-down",
        parent: parentNode
      };

      Object.defineProperty(loadMoreNode, '$parentNode', {
        value: 'static',
        writable: true
      });

      loadMoreNode.$parentNode = parentNode;
      loadMoreNode.parent = parentNode;
      loadedNodes.push(loadMoreNode);
    }

    return loadedNodes;
  };

  private mapRootChildren(roots: any[], rootNode: any) {
    roots.filter((current: any) => {
      if (
        current['sif~glavna_klasifikacija~parent_id'] != null &&
        rootNode.data['sif~glavna_klasifikacija~id'] ==
          current['sif~glavna_klasifikacija~parent_id']
      ) {
        const node: EarhivTreeNode = {};
        node['data'] = current;
        node['icon'] = 'pi pi-fw pi-book';
        node['children'] = [];
        node['leaf'] = false;
        node['selectable'] = false;
        node['offset'] = 0;
        rootNode.children.push(node);
        this.mapRootChildren(roots, node);
        (node as any)['loadJedinice'] = node.children.length == 0;
        // definiramo vezu prema parent node-u na podacima, ali preko defineProperty da izbjegnemo cirkularnu serijalizaciju u JSON
        Object.defineProperty(node, '$parentNode', {
          value: 'static',
          writable: true
        });
        (node as any).$parentNode = rootNode;
        // TODO ovdje bi trebalo rekurzivno proci do root parent-a i dohvatiti svu sifru od root-a do trenutnog node-a pa je tako i prikazati
        // npr.
        // A Nesto
        // A.1 Nesto drugo
        // A.1.1 Nesto trece
        node['label'] =
          current['sif~glavna_klasifikacija~sifra'] +
            ' ' +
            current['sif~glavna_klasifikacija~naziv'] || '';
        return false;
      }
      return false;
    });
    return rootNode;
  }

  onNodeDrop(event: any) {
    if (event.dropNode.leaf) {
      console.log('onDrop', event, event.node);

      this.msg.add({
        severity: 'error',
        summary: 'Odaberite odgovarajući folder.',
        detail: 'Odaberite odgovarajući folder.'
      });
    } else {
      event.accept();
      this.tree.updateSerializedValue();
    }
  }

  loadMore(node: any) {
    console.log('testLoadMore ');

    const glavnaKlasifikacijaId = node.parent.data[
      'sif~glavna_klasifikacija~id'
    ]
      ? node.parent.data['sif~glavna_klasifikacija~id']
      : null;
    if (!!glavnaKlasifikacijaId && !node.parent['loadJedinice']) {
      // rijec je o nodeu glavne klasifikacije, kod njih expandamo samo zadnju razinu
      return;
    }
    const jedinicaGradivaId = node.parent.data['en~jedinica_gradiva~id']
      ? node.parent.data['en~jedinica_gradiva~id']
      : null;

    const requestState = glavnaKlasifikacijaId
      ? Object.assign({}, this.listViewProvider.viewState, {
          'en~jedinica_gradiva~arhivska_klasifikacija': glavnaKlasifikacijaId,
          $paging_offset: node.parent.offset,
          $paging_limit: 100
        })
      : jedinicaGradivaId
      ? Object.assign({}, this.listViewProvider.viewState, {
          'en~jedinica_gradiva~nadredena_jedinica_gradiva': jedinicaGradivaId,
          $paging_offset: node.parent.offset,
          $paging_limit: 100
        })
      : null;

    console.log('reqst', requestState);

    this.listViewProvider
      .loadMore(node.parent, requestState, 100)
      .subscribe((res) => {
        if (res.length == 0) {
          this.msg.add({
            severity: 'info',
            summary: 'Obavijest',
            detail: `Učitali ste sve podatke za ${node.parent.label}`
          });
        }
      });
  }

  nodeExpand(event: any) {
    const glavnaKlasifikacijaId = event.node.data['sif~glavna_klasifikacija~id']
      ? event.node.data['sif~glavna_klasifikacija~id']
      : null;
    if (!!glavnaKlasifikacijaId && !event.node['loadJedinice']) {
      // rijec je o nodeu glavne klasifikacije, kod njih expandamo samo zadnju razinu
      return;
    }
    const jedinicaGradivaId = event.node.data['en~jedinica_gradiva~id']
      ? event.node.data['en~jedinica_gradiva~id']
      : null;

    const requestState = glavnaKlasifikacijaId
      ? Object.assign({}, this.listViewProvider.viewState, {
          'en~jedinica_gradiva~arhivska_klasifikacija': glavnaKlasifikacijaId
        })
      : jedinicaGradivaId
      ? Object.assign({}, this.listViewProvider.viewState, {
          'en~jedinica_gradiva~nadredena_jedinica_gradiva': jedinicaGradivaId
        })
      : {
          $paging_offset: 0,
          $paging_limit: 100
        };

    const isMojeGradivo =
      this.route.snapshot.data['isMojeGradivo'] ||
      this.routeState['isMojeGradivo'];
    if (isMojeGradivo) {
      Object.assign(requestState, { $filterMojeGradivo: isMojeGradivo });
    }

    this.loading = true;
    this.listViewProvider
      .nodeExpand(event.node, requestState, 100)
      .pipe(
        tap(() => {
          this.loading = false;
        })
      )
      .subscribe();
  }

  nodeCollapse(event: any) {
    this.listViewProvider.nodeCollapse(event.node);
  }

  get selectedFiles() {
    return this._selectedFiles;
  }

  set selectedFiles(values) {
    if (this.listViewProvider) {
      this.listViewProvider.selectedItems = values.map((node) => node.data);
    }
    this._selectedFiles = values;
  }

  protected getInitRootViewState() {
    const isMojeGradivo = this.route.snapshot.data['isMojeGradivo'];
    if (isMojeGradivo) {
      return Object.assign({}, INITAL_ROOT_VIEWSTATE, {
        $filterMojeGradivo: isMojeGradivo
      });
    }
    return Object.assign({}, INITAL_ROOT_VIEWSTATE);
  }

  private initNavigationStateSubscription() {
    this.navigationSubscription = this.router.events.subscribe((event: any) => {
      if (event.routerEvent instanceof NavigationEnd) {
        const state = this.router.lastSuccessfulNavigation?.extras?.state;

        if (state) {
          this.routeState = state;
        }
      }
    });
  }
}
